import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from '../Components/Modal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { useAuth } from '../AuthContext';

const spriteUrl = id => `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${id}.png`;
const smallSprite = id => `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${id}.png`;

function SoulLinkGame() {
  const { user } = useAuth();
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [runs, setRuns] = useState([]);
  const [finalTeam, setFinalTeam] = useState([]);
  const [finalRun, setFinalRun] = useState(null);
  const [topPokemon, setTopPokemon] = useState([]);
  const [allPairs, setAllPairs] = useState([]);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addNumber, setAddNumber] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addStatus, setAddStatus] = useState('');
  const [editingRun, setEditingRun] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchGame = async () => {
    const sgRes = await fetch(
      `${SUPABASE_URL}/rest/v1/soul_link_games?id=eq.${gameId}&select=*`,
      { headers: supabaseHeaders(false) }
    );
    const sgData = await sgRes.json();
    const pokemonGameId = sgData[0]?.pokemon_game_id;
    if (!pokemonGameId) return;

    const pgRes = await fetch(
      `${SUPABASE_URL}/rest/v1/pokemon_games?id=eq.${pokemonGameId}&select=*`,
      { headers: supabaseHeaders(false) }
    );
    const pgData = await pgRes.json();
    setGame(pgData[0] || null);
  };

  const fetchRuns = async () => {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/soul_link_runs?soul_link_game_id=eq.${gameId}&order=created_at.desc&select=*`,
      { headers: supabaseHeaders(false) }
    );
    const data = await res.json();
    const all = Array.isArray(data) ? data : [];
    setRuns(all);

    const fr = all.find(r => r.is_final) || null;
    setFinalRun(fr);
    if (fr) {
      const pRes = await fetch(
        `${SUPABASE_URL}/rest/v1/soul_link_pairs?run_id=eq.${fr.id}&is_final_team=eq.true&order=created_at.asc&select=*`,
        { headers: supabaseHeaders(false) }
      );
      const pData = await pRes.json();
      setFinalTeam(Array.isArray(pData) ? pData : []);
    }

    if (all.length > 0) {
      const ids = all.map(r => r.id).join(',');
      const pairRes = await fetch(
        `${SUPABASE_URL}/rest/v1/soul_link_pairs?run_id=in.(${ids})&select=*`,
        { headers: supabaseHeaders(false) }
      );
      const pairData = await pairRes.json();
      const counts = {};
      (Array.isArray(pairData) ? pairData : []).forEach(pair => {
        [
          { id: pair.p1_pokemon_id, name: pair.p1_name },
          { id: pair.p2_pokemon_id, name: pair.p2_name },
        ].forEach(({ id, name }) => {
          if (!id) return;
          if (!counts[id]) counts[id] = { name, pokemonId: id, count: 0 };
          counts[id].count++;
        });
      });
      setTopPokemon(Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 6));
      setAllPairs(Array.isArray(pairData) ? pairData : []);
    }
  };

  useEffect(() => {
    fetchGame();
    fetchRuns();
  }, [gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setAddNumber('');
    setAddStatus('');
    setShowAdd(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addNumber) { setAddStatus('Fill in a number.'); return; }
    setAddLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/soul_link_runs`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
        body: JSON.stringify({
          soul_link_game_id: gameId,
          name: `Run ${addNumber}`,
          player1: 'Ghoul',
          player2: 'Arty',
        }),
      });
      if (!res.ok) throw new Error('Failed to create');
      const data = await res.json();
      const newRun = Array.isArray(data) ? data[0] : data;
      setRuns(prev => [newRun, ...prev]);
      setShowAdd(false);
    } catch (err) {
      setAddStatus(`Error: ${err.message}`);
    } finally {
      setAddLoading(false);
    }
  };

  const deleteRun = async (id) => {
    setDeletingId(id);
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/soul_link_runs?id=eq.${id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      setEditingRun(null);
      setRuns(prev => prev.filter(r => r.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  if (!game) return null;

  return (
    <div className="app-container">
      <div className="header">
        <h1>{game.name}</h1>
        <div className="controls">
          <button onClick={() => navigate('/pokemon/soullink')} className="header-nav-btn">←</button>
          {user && <button onClick={openAdd}>Add +</button>}
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {finalRun && finalTeam.length > 0 && (
        <div className="soul-link-final-section">
          <div className="soul-link-final-header">
            <span>Final Team — {finalRun.name}</span>
          </div>
          <div className="soul-link-grid">
            {finalTeam.map(pair => (
              <div key={pair.id} className="soul-link-card">
                {pair.pair_name && <div className="soul-link-pair-label">{pair.pair_name}</div>}
                <div className="soul-link-slots">
                  <div className="soul-link-slot">
                    <img
                      src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pair.p1_pokemon_id}.png`}
                      alt={pair.p1_name}
                      className="soul-link-img"
                      onError={e => { e.target.onerror = null; e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pair.p1_pokemon_id}.png`; }}
                    />
                    <span className="soul-link-player">Ghoul</span>
                    <span className="soul-link-poke-name">{pair.p1_name}</span>
                  </div>
                  <span className="soul-link-heart">X</span>
                  <div className="soul-link-slot">
                    <img
                      src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pair.p2_pokemon_id}.png`}
                      alt={pair.p2_name}
                      className="soul-link-img"
                      onError={e => { e.target.onerror = null; e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pair.p2_pokemon_id}.png`; }}
                    />
                    <span className="soul-link-player">Arty</span>
                    <span className="soul-link-poke-name">{pair.p2_name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {topPokemon.length > 0 && (
        <div className="soul-link-stats-section">
          <div className="soul-link-stats-grid">
            {topPokemon.map(p => (
              <div key={p.pokemonId} className="soul-link-stat-card">
                <img
                  src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${p.pokemonId}.png`}
                  alt={p.name}
                  className="soul-link-stat-img"
                  onError={e => { e.target.onerror = null; e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${p.pokemonId}.png`; }}
                />
                <span className="soul-link-stat-name">{p.name}</span>
                <span className="soul-link-stat-count">{p.count}×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {search && (
        <div className="soul-link-grid" style={{ marginTop: '1rem' }}>
          {allPairs.filter(p => {
            const q = search.toLowerCase();
            return (
              p.p1_name?.toLowerCase().includes(q) ||
              p.p2_name?.toLowerCase().includes(q) ||
              p.pair_name?.toLowerCase().includes(q)
            );
          }).map(pair => {
            const run = runs.find(r => r.id === pair.run_id);
            return (
              <div key={pair.id} className="soul-link-card">
                {run && <div className="soul-link-pair-label" style={{ color: '#888' }}>{run.name}</div>}
                {pair.pair_name && <div className="soul-link-pair-label">{pair.pair_name}</div>}
                <div className="soul-link-slots">
                  <div className="soul-link-slot">
                    <img src={spriteUrl(pair.p1_pokemon_id)} alt={pair.p1_name} className="soul-link-img" onError={e => { e.target.onerror = null; e.target.src = smallSprite(pair.p1_pokemon_id); }} />
                    <span className="soul-link-player">Ghoul</span>
                    <span className="soul-link-poke-name">{pair.p1_name}</span>
                  </div>
                  <span className="soul-link-heart">X</span>
                  <div className="soul-link-slot">
                    <img src={spriteUrl(pair.p2_pokemon_id)} alt={pair.p2_name} className="soul-link-img" onError={e => { e.target.onerror = null; e.target.src = smallSprite(pair.p2_pokemon_id); }} />
                    <span className="soul-link-player">Arty</span>
                    <span className="soul-link-poke-name">{pair.p2_name}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="todo-grid">
        {runs.map((run, idx) => (
          <div
            className="todo-card"
            key={run.id}
            style={{ '--index': idx }}
            onClick={() => navigate(`/pokemon/soullink/${gameId}/${run.id}`)}
            onDoubleClick={user ? e => { e.stopPropagation(); setEditingRun(run); } : undefined}
          >
            {run.is_final && (
              <div className="soul-link-final-badge">Final Run</div>
            )}
            <div className="todo-card-title">{run.name}</div>
            <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem' }}>
              Ghoul &amp; Arty
            </div>
          </div>
        ))}
      </div>

      {runs.length === 0 && <p className="no-results">Geen ene tering hier man.</p>}

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)}>
          <h2 className="modal-title">New Run</h2>
          <form className="modal-form" onSubmit={handleAdd}>
            <div className="form-group">
              <label>Run number</label>
              <input
                type="number"
                min="1"
                value={addNumber}
                onChange={e => setAddNumber(e.target.value)}
                
                autoFocus
                required
              />
            </div>
            {addStatus && <p className="form-status">{addStatus}</p>}
            <div className="edit-modal-actions">
              <button type="submit" className="form-submit" disabled={addLoading}>
                {addLoading ? 'Saving...' : 'Add'}
              </button>
              <button type="button" className="form-submit btn-cancel" onClick={() => setShowAdd(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editingRun && (
        <Modal onClose={() => setEditingRun(null)}>
          <h2 className="modal-title">{editingRun.name}</h2>
          <div className="modal-form">
            <div className="edit-modal-actions">
              <button
                className="form-submit btn-delete"
                disabled={deletingId === editingRun.id}
                onClick={() => deleteRun(editingRun.id)}
              >
                {deletingId === editingRun.id ? 'Deleting...' : 'Delete'}
              </button>
              <button className="form-submit btn-cancel" onClick={() => setEditingRun(null)}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default SoulLinkGame;
